// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/Thread.test.tsx
// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ChatPage.test.tsx
import { CheckCircle } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { type AskV2RailFinding } from "../rail/types";
import { AskV2ReviewCommentJudgmentActions } from "../review/AskV2ReviewCommentJudgmentActions";
import {
  useAskPunttReview,
  useUpdateAskPunttReviewComment,
} from "../review/useAskPunttReview";

/**
 * Same footprint as the static tool-result row in `AgentSteps` (`ToolResultStep`
 * when it is not an artifact link): `TOOL_CHIP_CLASS` plus the done check and
 * name/secondary labels. ask-v2 cannot import `routes/ask-puntt` (import wall),
 * so the classes are copied rather than a new card.
 */
const TOOL_CALL_ROW_CLASS =
  "flex w-full min-w-0 items-center gap-2 rounded-lg bg-transparent px-[11px] py-[7px]";

/**
 * The judge control for ONE finding, once christo has written its comment.
 *
 * Its own component, and mounted only for a finding that names a review, so the
 * review read this needs is issued only where there is something to join — a
 * finding in the first minutes of a turn has no `reviewId` and no control, and
 * this block must not open a query for it.
 *
 * The judgment is written against the COMMENT, which christo writes a poll
 * after warhol publishes — so a finding is joined to its comment by `intentId`
 * (the key both sides carry) and the control appears on the row as soon as that
 * write lands. It is the same join, the same mutation and the same control the
 * review workspace's own list uses (`AskV2ReviewCommentJudgmentActions` over
 * `useUpdateAskPunttReviewComment`), on purpose: one judgment has one writer,
 * and a second spelling of it here is a second thing to keep in step with the
 * document `settleWarholTurnReview` merges into.
 */
function ThreadFindingJudgment({
  reviewId,
  intentId,
}: {
  reviewId: string;
  intentId: string;
}) {
  const { data } = useAskPunttReview(reviewId);
  const updateComment = useUpdateAskPunttReviewComment(reviewId);

  const comment = useMemo(() => {
    const found = (data?.review.comments ?? []).find(
      (item) => item?.intentId === intentId,
    );
    return found ? { id: found._id, liked: found.liked } : null;
  }, [data, intentId]);

  if (!comment) return null;

  return (
    <AskV2ReviewCommentJudgmentActions
      commentId={comment.id}
      liked={comment.liked}
      rejected={comment.liked === false}
      disabled={updateComment.isPending}
      // why: always drawn, never on hover. The thread is a reading surface with
      // no per-row hover chrome, and the whole point of the control is that she
      // can act on a finding while the rest of the review still runs.
      pinned
      onJudge={(liked) =>
        updateComment.mutate({ commentId: comment.id, body: { liked } })
      }
      // why: direct, not through the workspace's reject-scope prompt. That
      // prompt chooses whether a rejection is REMEMBERED for later reviews,
      // which is a review-document decision; the thread row only has to record
      // the judgment.
      onRequestDelete={() =>
        updateComment.mutate({ commentId: comment.id, body: { liked: false } })
      }
    />
  );
}

/**
 * Early verified findings, in the thread.
 *
 * The row is a finding warhol published the moment its evidence verifier
 * accepted it — minutes before the turn settles — and the promise it is here to
 * keep is "urgent findings show up as Puntt finds them, **so she can start
 * judging before it is done**". A row that can only be read keeps the first half
 * of that and drops the second: the finding she just watched appear is the one
 * she has an opinion about, and the surface she is already looking at is this
 * one. So each row carries a judge control (see `ThreadFindingJudgment`).
 *
 * `reviewId` on the finding is the review its provisional comments hang off; it
 * is absent for the first minutes of a turn, which is the window where the row
 * is drawn without a control rather than with a dead one.
 */
export function AskV2ThreadFindings({
  findings,
  readOnly = false,
}: {
  findings: AskV2RailFinding[];
  /**
   * Read-only viewer. A judgment is a POST the backend 403s for this viewer and
   * no transport gate can swallow (`useUpdateAskPunttReviewComment` writes
   * straight to christo), so the control is withheld rather than drawn dead —
   * the same refusal `AskV2Thread`'s own docblock applies to the failed-row
   * Retry. Defaults to `false` so a caller that has not been taught the flag
   * keeps today's behaviour.
   */
  readOnly?: boolean;
}) {
  const { t } = useTranslation("ui");
  const confidence = (severity: string) =>
    severity === "critical" || severity === "major"
      ? t("highConfidence")
      : severity === "minor"
        ? t("mediumConfidence")
        : t("lowConfidence");

  return (
    <div
      className="my-3"
      data-testid="ask-v2-thread-found-so-far"
      role="status"
      aria-live="polite"
    >
      <Text
        as="p"
        size="1"
        weight="regular"
        className="px-[11px] text-puntt-neutral-gray-12"
        data-testid="ask-v2-thread-found-so-far-title"
      >
        {t("askV2.rail.foundSoFar")}
      </Text>
      <div className="mt-1.5 flex flex-col gap-1.5">
        {findings.map((finding) => {
          const label = confidence(finding.severity);
          const reviewId = !readOnly ? finding.reviewId : undefined;
          return (
            <div
              key={finding.intentId}
              className={TOOL_CALL_ROW_CLASS}
              data-testid={`ask-v2-thread-finding-${finding.intentId}`}
              aria-label={t("askV2.rail.findingAria", {
                severity: label,
                description: finding.description,
              })}
            >
              <CheckCircle
                size={14}
                weight="fill"
                className="shrink-0 text-puntt-neutral-grayA-11"
                aria-hidden
              />
              <Text
                as="span"
                size="1"
                weight="regular"
                title={label}
                className="min-w-0 max-w-[45%] shrink-0 truncate whitespace-nowrap text-puntt-neutral-gray-12"
              >
                {label}
              </Text>
              <Text
                as="span"
                size="1"
                title={finding.description}
                className="min-w-0 flex-1 truncate whitespace-nowrap text-puntt-neutral-gray-9"
              >
                {finding.description}
              </Text>
              {reviewId ? (
                <ThreadFindingJudgment
                  reviewId={reviewId}
                  intentId={finding.intentId}
                />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
